import {
  Button,
  Card,
  CardHeader,
  colors,
  Divider,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";

import AddIcon from "@material-ui/icons/AddOutlined";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import firebase from "firebase/app";
import _ from "lodash";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import uuidv4 from "uuid/v4";
import { w3cwebsocket as W3CWebSocket } from "websocket";

import FilterList from "../FilterList.js";
import useEnvironment from "../hooks/use-environment";
import Label from "../Label";
import Navigation from "../Navigation.js";
import { iconForEventType, masqueradeToken } from "../util";
import { canDefineOrView, EventDetails } from "./EventDetailsDrawer";
import { LIVEVIEW_URL } from "../config.js";
import { DefineEvent, ViewEvent } from "./shared";

function configureLiveView(conn, searchString, filter, envID) {
  firebase
    .auth()
    .currentUser.getIdToken()
    .then((token) => {
      const msg = {
        action: "configure",
        token: token,
        email: firebase.auth().currentUser.email,
        search_string: searchString,
        filter: filter,
        envID: envID,
      };

      if (masqueradeToken()) {
        msg.masquerade_token = masqueradeToken();
      }

      conn.send(JSON.stringify(msg));
    });
}

function Index() {
  const alert = useAlert();
  const [conn, setConn] = useState(null);
  const [searchString, setSearchString] = useState("");
  const [eventsState, setEventsState] = useState({
    events: [],
    paused: false,
    lastEventTime: 0,
    tokensLeft: 0,
  });
  const [filters, setFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const [eventDrilldownData, setEventDrilldownData] = useState(null);
  const currentEnvironment = useEnvironment();

  // Initialize the live view websocket.
  useEffect(() => {
    const client = new W3CWebSocket(LIVEVIEW_URL);
    client.onopen = () => {
      setConn(client);
    };

    client.onmessage = (message) => {
      setEventsState((state) => {
        if (state.paused) {
          return state;
        }

        // We set a maximum rate of 5 events/second.
        const curTime = moment().unix();
        const newTokens = (curTime - state.lastEventTime) * 5;
        const tokensLeft = Math.min(newTokens + state.tokensLeft, 5);
        if (tokensLeft < 0) {
          return state;
        }

        var event = JSON.parse(message.data);
        event = _.extend({}, event, { id: uuidv4() });
        return _.extend({}, state, {
          events: [event].concat(state.events).slice(0, 50),
          lastEventTime: curTime,
          tokensLeft: tokensLeft - 1,
        });
      });
    };

    return function cleanup() {
      client.close();
    };
  }, [currentEnvironment.ID]);

  useEffect(() => {
    setEventsState({
      events: [],
      paused: false,
      lastEventTime: 0,
      tokensLeft: 0,
    });
  }, [currentEnvironment.ID]);

  function handlePausedChange() {
    setEventsState((state) => {
      return _.extend({}, state, { paused: !state.paused });
    });
  }

  function handleClear() {
    setEventsState((state) => {
      return _.extend({}, state, { events: [] });
    });
  }

  function handleApplyFilter() {
    for (var filter of filters) {
      if (!filter.prop) {
        alert.error(
          "The property for one of the event definition filters is unselected."
        );
        return;
      }

      if (!filter.op) {
        alert.error(
          "The operator for one of the event definition filters is unselected."
        );
        return;
      }
    }

    setActiveFilters(filters);
    alert.success("Filter Applied");
  }

  function quickAddFilter(prop, op, val) {
    const newFilter = {
      type: "prop_filter",
      prop,
      op,
      val,
    };

    const newFilters = [...filters].concat([newFilter]);
    setFilters(newFilters);

    const newActiveFilters = [...activeFilters].concat([newFilter]);
    setActiveFilters(newActiveFilters);

    alert.success("Filter Applied");
  }

  // Restrict the filtering on the live view.
  useEffect(() => {
    if (conn !== null) {
      const envID = currentEnvironment.loaded ? currentEnvironment.ID : null;
      configureLiveView(
        conn,
        searchString,
        {
          type: "and",
          filters: activeFilters,
        },
        envID
      );
    }
  }, [conn, searchString, activeFilters, currentEnvironment]);

  if (conn === null) {
    return null;
  } else {
    return (
      <LiveViewUI
        eventDrilldownData={eventDrilldownData}
        setEventDrilldownData={setEventDrilldownData}
        events={eventsState.events}
        searchString={searchString}
        onSearchStringChange={setSearchString}
        filters={filters}
        onFiltersChange={setFilters}
        paused={eventsState.paused}
        onPausedChange={handlePausedChange}
        onClear={handleClear}
        onApplyFilter={handleApplyFilter}
        quickAddFilter={quickAddFilter}
      />
    );
  }
}

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    display: "flex",
    alignItems: "center",
  },
  cardHeaderOpen: {
    paddingRight: 350,
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  tableRowExpanded: {
    backgroundColor: "#f0f0f0",
  },
  tableCell: {
    paddingTop: 6,
    paddingBottom: 6,
  },
  tableCellDetails: {
    display: "flex",
  },
  tableCellDetailsOpen: {
    justifyContent: "center",
  },
  tableCellDetailsClosed: {
    justifyContent: "center",
  },
  tableCellEllipsis: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  tableCellEventActions: {
    whiteSpace: "nowrap",
  },
  tableCellUserId: {
    maxWidth: 150,
  },
  tableCellEventName: {
    maxWidth: 250,
  },
  cardTitle: {
    paddingRight: 15,
  },
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
    verticalAlign: "middle",
  },
  inlineIcon: {
    verticalAlign: "middle",
    marginRight: theme.spacing(1),
    fill: "#797979",
  },
  bold: {
    fontWeight: 600,
  },
  flexGrow: {
    flexGrow: 1,
  },
  pauseButton: {
    marginRight: 15,
    marginLeft: 15,
  },
  filters: {
    padding: 20,
  },
  applyFilterButton: {
    marginLeft: 15,
    height: 34,
  },
  card: {
    overflowX: "auto",
  },
}));

function LiveViewUI({
  events,
  filters,
  onFiltersChange,
  searchString,
  onSearchStringChange,
  paused,
  onPausedChange,
  onClear,
  onApplyFilter,
  eventDrilldownData,
  setEventDrilldownData,
  quickAddFilter,
}) {
  const classes = useStyles();

  function handleAddFilter() {
    onFiltersChange((filters) => {
      const newFilter = {
        type: "prop_filter",
        prop: "",
        op: "",
        val: "",
      };

      const newFilters = filters.concat([newFilter]);
      return newFilters;
    });
  }

  const drawerOpen = !_.isEmpty(eventDrilldownData);

  return (
    <>
      <Card className={clsx(classes.card, "fs-exclude")}>
        <CardHeader
          title={
            <div
              className={clsx(
                classes.cardHeader,
                drawerOpen && classes.cardHeaderOpen
              )}
            >
              <div className={clsx(classes.cardTitle)}>Live View</div>
              <TextField
                variant="outlined"
                autoComplete="off"
                name="search"
                onChange={(e) => onSearchStringChange(e.target.value)}
                value={searchString}
                InputProps={{
                  className: clsx(classes.searchField),
                  placeholder: "Search",
                  startAdornment: (
                    <InputAdornment>
                      <SearchIcon className={clsx(classes.icon)} />
                    </InputAdornment>
                  ),
                }}
              />
              <div className={clsx(classes.flexGrow)} />

              {paused ? (
                <Label color={colors.grey[600]}>Paused</Label>
              ) : (
                <Label color={colors.green[600]}>Running</Label>
              )}

              <Button
                className={clsx(classes.pauseButton)}
                color="primary"
                onClick={onPausedChange}
                variant="contained"
              >
                {paused ? "Resume" : "Pause"}
              </Button>
              <Button onClick={onClear} variant="contained">
                Clear
              </Button>
            </div>
          }
        />
        <Divider />
        <Grid
          container
          alignItems="flex-start"
          alignContent="flex-start"
          className={clsx(classes.filters)}
        >
          <Grid item xs={1}>
            <Typography variant="h5">Filters</Typography>
          </Grid>
          <Grid item xs={5}>
            <FilterList filters={filters} setState={onFiltersChange} />
            <div>
              <Button
                color="default"
                onClick={handleAddFilter}
                variant="outlined"
              >
                <AddIcon className={classes.icon} />
                Add Filter
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={onApplyFilter}
                className={clsx(classes.applyFilterButton)}
              >
                Apply Filters
              </Button>
            </div>
          </Grid>
        </Grid>
        <EventList
          eventDrilldownData={eventDrilldownData}
          setEventDrilldownData={setEventDrilldownData}
          events={events}
        />
      </Card>
      <EventDetails
        handleAddFilter={quickAddFilter}
        eventDrilldownData={eventDrilldownData}
        setEventDrilldownData={setEventDrilldownData}
      />
    </>
  );
}

LiveViewUI.propTypes = {
  events: PropTypes.array.isRequired,
  filters: PropTypes.array.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  paused: PropTypes.bool.isRequired,
  onPausedChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onApplyFilter: PropTypes.func.isRequired,
  eventDrilldownData: PropTypes.object,
  setEventDrilldownData: PropTypes.func.isRequired,
  quickAddFilter: PropTypes.func.isRequired,
};

const Row = React.memo(function Row({ event, isSelected, setSelected }) {
  const classes = useStyles();

  const toggleExpanded = () => {
    if (isSelected) {
      setSelected(null);
    } else {
      setSelected({ ...event });
    }
  };

  var title;
  if (event.type === "click") {
    title = "Click";
  } else if (event.type === "pageview") {
    title = "Pageview";
  } else if (event.type === "submit") {
    title = "Submit";
  } else if (event.type === "change") {
    title = "Change";
  } else if (event.type === "touch") {
    title = "Touch";
  } else if (event.type === "text_edit") {
    title = "Edit Text";
  } else if (event.type === "toggle") {
    title = "Toggle Switch";
  } else if (event.type === "scroll_view_page") {
    title = "Scroll";
  } else if (event.type === "react_navigation_screenview") {
    title = "Screen View";
  } else {
    title = "Precision Tracking";
  }

  const Icon = iconForEventType(event.type);

  return (
    <TableRow
      onClick={toggleExpanded}
      className={clsx(classes.tableRow, isSelected && classes.tableRowExpanded)}
    >
      <TableCell
        className={clsx(
          classes.tableCellEventName,
          classes.tableCellEllipsis,
          classes.tableCell,
          event.event_def_id && classes.bold
        )}
      >
        <Tooltip title={title}>
          <Icon height={24} width={24} className={classes.inlineIcon} />
        </Tooltip>
        <Tooltip title={event.event_name}>
          <span>{event.event_name}</span>
        </Tooltip>
      </TableCell>
      <TableCell
        className={clsx(
          classes.tableCellUserId,
          classes.tableCellEllipsis,
          classes.tableCell
        )}
      >
        {event.distinct_id}
      </TableCell>
      <TableCell className={clsx(classes.tableCellEllipsis, classes.tableCell)}>
        {event.path}
      </TableCell>

      <TableCell
        className={clsx(classes.tableCellEventActions, classes.tableCell)}
        align="center"
      >
        {
          // If we are showing a built-in event, you can't define a new event
          // based on it, or view the event definition for it.
          canDefineOrView(event) &&
            (event.event_def_id ? (
              <ViewEvent eventDefID={event.event_def_id} />
            ) : (
              <DefineEvent
                type={event.type}
                typeSpecificProperty={event.type_specific_property}
              />
            ))
        }
      </TableCell>
    </TableRow>
  );
});

Row.propTypes = {
  event: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  setSelected: PropTypes.func.isRequired,
};

function EventList({ events, eventDrilldownData, setEventDrilldownData }) {
  const rows = events.map((event) => {
    const isSelected = !!(
      eventDrilldownData && eventDrilldownData.id === event.id
    );
    return (
      <Row
        isSelected={isSelected}
        key={event.id}
        event={event}
        setSelected={setEventDrilldownData}
      />
    );
  });

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Event</TableCell>
          <TableCell>User ID</TableCell>
          <TableCell>Path</TableCell>
          <TableCell align="center">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>{rows}</TableBody>
    </Table>
  );
}

EventList.propTypes = {
  events: PropTypes.array.isRequired,
  setEventDrilldownData: PropTypes.func.isRequired,
  eventDrilldownData: PropTypes.object,
};

const navigationStyles = makeStyles(() => ({
  navigation: {
    paddingTop: 24,
  },
}));

function LiveViewPageWithNavigation() {
  const classes = navigationStyles();

  return (
    <Navigation className={classes.navigation}>
      <Index />
    </Navigation>
  );
}

export default LiveViewPageWithNavigation;
